import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { WsContext, type WsContextShape } from "./context";

type EventHandler = (payload: unknown) => void;

function wsUrl(): string {
  const env = import.meta.env.VITE_WS_URL as string | undefined;
  if (env) return env;
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${window.location.host}/ws`;
}

export function WebSocketProvider({
  enabled,
  children,
}: {
  enabled: boolean;
  children: ReactNode;
}) {
  const socketRef = useRef<WebSocket | null>(null);
  const handlersRef = useRef(new Map<string, Set<EventHandler>>());
  const heartbeatRef = useRef<number | null>(null);
  const reconnectRef = useRef(0);
  const [connected, setConnected] = useState(false);
  const [disconnectedSince, setDisconnectedSince] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let closed = false;

    function connect() {
      if (closed) return;
      const s = new WebSocket(wsUrl());
      socketRef.current = s;

      s.onopen = () => {
        reconnectRef.current = 0;
        setConnected(true);
        setDisconnectedSince(null);
        if (heartbeatRef.current !== null) window.clearInterval(heartbeatRef.current);
        heartbeatRef.current = window.setInterval(() => {
          if (s.readyState === WebSocket.OPEN) s.send(JSON.stringify({ type: "ping" }));
        }, 30_000);
      };

      s.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (!data || typeof data.type !== "string") return;
          const listeners = handlersRef.current.get(data.type);
          if (listeners) listeners.forEach((fn) => fn(data.data ?? data));
        } catch {
          // ignore malformed frames
        }
      };

      const scheduleReconnect = () => {
        if (closed) return;
        reconnectRef.current = Math.min(reconnectRef.current + 1, 6);
        const backoff = Math.min(30_000, 1000 * 2 ** reconnectRef.current);
        window.setTimeout(connect, backoff);
      };

      s.onclose = () => {
        if (heartbeatRef.current !== null) {
          window.clearInterval(heartbeatRef.current);
          heartbeatRef.current = null;
        }
        setConnected(false);
        // Only stamp the first transition into a disconnected state — repeated
        // close events during reconnect attempts shouldn't reset the timer the
        // banner uses to decide when to surface itself.
        setDisconnectedSince((prev) => prev ?? Date.now());
        scheduleReconnect();
      };
      s.onerror = () => s.close();
    }

    connect();

    return () => {
      closed = true;
      if (heartbeatRef.current !== null) window.clearInterval(heartbeatRef.current);
      socketRef.current?.close();
      socketRef.current = null;
      setConnected(false);
    };
  }, [enabled]);

  const api = useMemo<WsContextShape>(
    () => ({
      subscribe(eventType, handler) {
        let set = handlersRef.current.get(eventType);
        if (!set) {
          set = new Set();
          handlersRef.current.set(eventType, set);
        }
        set.add(handler);
        return () => {
          set?.delete(handler);
        };
      },
      send(message) {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          socketRef.current.send(JSON.stringify(message));
        }
      },
      connected,
      disconnectedSince,
    }),
    [connected, disconnectedSince],
  );

  return <WsContext.Provider value={api}>{children}</WsContext.Provider>;
}
